import type { GameStateManager } from '../game/GameState';
import type { GameData, GameSpeed } from '../game/types';

export class HowToPlayModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(): void {
    const howToPlay = this.gameData.howToPlay;
    const sections = howToPlay.sections || [];

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content how-to-play-modal" tabindex="0">
        <div class="modal-header">
          <h2>${howToPlay.title || 'How to Play'}</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          ${sections.map((section: { title: string; content: string }) => `
            <div class="how-to-play-section">
              <h3>${section.title || ''}</h3>
              <div class="how-to-play-content">${this.formatContent(section.content || '')}</div>
            </div>
          `).join('')}
        </div>
        <div class="modal-footer">
          <button class="button-confirm" data-action="close">Close</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }

    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  private formatContent(content: string): string {
    // Convert line breaks to <br> tags and preserve formatting
    return content.split('\n').map(line => {
      line = line.trim();
      if (line.startsWith('•')) {
        return `<div class="how-to-play-bullet">${line}</div>`;
      }
      if (line === '') {
        return '<br>';
      }
      return `<div>${line}</div>`;
    }).join('');
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }
}
